import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Card,
  CardGrid,
  DateField,
  EmptyState,
  Page,
  PageHeader,
  Select,
  StatCard,
  StatCardList,
  Table,
  backLinkClasses,
  type Column,
} from '@evogard/ui';
import { Chart } from '@evogard/ui/chart';
import {
  DESK_BANDS,
  DESK_BAND_LABELS,
  deskEfficiency,
  format as formatMoney,
  formatDate,
  loadFailureMessage,
  sum,
} from '@evogard/domain';
import type { DeskDashboard } from '@evogard/contract';
import { api } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';
import {
  mergeDashboards,
  monthLabel,
  monthRange,
  recentMonths,
  today,
  weekOf,
  type LoadColumn,
  type MergedPerson,
  type PeriodKind,
  type Tally,
} from './deskDashboard.js';
import { DESK_KEY } from './useDesk.js';

/**
 * O aproveitamento da Mesa — o "Dashboard de Aproveitamento" do Kanban
 * (`App.tsx:2373-3481`), com CE e GO somadas, como lá.
 *
 * Quem vê é quem tem `billing.desk:acompanhar`. No Kanban só o admin via; aqui
 * é decisão de quem monta os grupos.
 *
 * As diferenças para o legado, de propósito, estão no docblock de
 * `billing.desk-dashboard.service.ts`. A que a equipe mais vai notar fica
 * escrita no rodapé da tela: o pago automático passa a ter valor.
 */
export function DeskDashboardScreen() {
  const [kind, setKind] = useState<PeriodKind>('mes');
  const [day, setDay] = useState(today());
  const [month, setMonth] = useState(today().slice(0, 7));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [everyone, setEveryone] = useState(false);

  const period =
    kind === 'dia'
      ? { from: day || today(), to: day || today() }
      : kind === 'semana'
        ? weekOf(day || today())
        : kind === 'mes'
          ? monthRange(month)
          : { from: from || today(), to: to || today() };
  const inverted = period.from > period.to;

  const { data: context } = useSessionContext();
  const tenants = context?.tenants ?? [];
  const results = useQueries({
    queries: tenants.map((t) => ({
      enabled: !inverted,
      queryKey: [...DESK_KEY, 'aproveitamento', t.slug, period.from, period.to],
      queryFn: async (): Promise<DeskDashboard | null> => {
        const r = await api.billingDesk.dashboard({ params: { tenant: t.slug }, query: period });
        if (r.status === 403 || r.status === 404) return null;
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, `o aproveitamento da ${t.nome}`));
        return r.body;
      },
      refetchInterval: 120_000,
    })),
  });

  const ready: DeskDashboard[] = [];
  const refused: string[] = [];
  const errors: string[] = [];
  results.forEach((r, i) => {
    if (r.error) errors.push(r.error.message);
    else if (r.data === null) refused.push(tenants[i]!.nome);
    else if (r.data) ready.push(r.data);
  });
  const loading = results.some((r) => r.isLoading);
  const merged = mergeDashboards(ready);

  const credited = (pick: (p: MergedPerson) => Tally, extra: Tally) =>
    merged.people.reduce(
      (t, p) => ({ count: t.count + pick(p).count, amount: sum(t.amount, pick(p).amount) }),
      extra,
    );
  const collection = credited((p) => p.paidCollection, merged.unattributed.paidCollection);
  const automatic = credited((p) => p.paidAutomatic, merged.unattributed.paidAutomatic);
  const closed = collection.count + automatic.count;
  const share = closed === 0 ? null : Math.round((collection.count / closed) * 100);
  /*
   * O Kanban somava só os pagos creditados a alguém: o automático que a fila
   * fechou sozinha ("Sistema") ficava fora. Em 01–19/10/2026 isso dava 72% lá
   * contra 43% aqui, com os mesmos cards. Até o dono escolher a definição, a
   * tela mostra as duas e diz o que cada uma conta.
   */
  const queueAutomatic = merged.unattributed.paidAutomatic;
  const creditedClosed = closed - queueAutomatic.count;
  const creditedShare =
    creditedClosed === 0
      ? null
      : Math.round(((collection.count - merged.unattributed.paidCollection.count) / creditedClosed) * 100);

  const shown = merged.people.filter((p) => everyone || p.listed);
  const nameOf = (userId: string | null) =>
    userId === null ? 'a fila mãe' : (merged.people.find((p) => p.userId === userId)?.name ?? 'pessoa sem cadastro');

  return (
    <Page measure="cheia">
      <PageHeader
        title="Aproveitamento da Mesa"
        back={
          <Link to="/billing/desk" className={backLinkClasses()}>
            Mesa de cobrança
          </Link>
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Select label="Período" value={kind} onChange={(e) => setKind(e.target.value as PeriodKind)}>
            <option value="dia">Por dia</option>
            <option value="semana">Por semana</option>
            <option value="mes">Por mês</option>
            <option value="intervalo">Intervalo</option>
          </Select>
        </div>
        {(kind === 'dia' || kind === 'semana') && (
          <div className="w-44">
            <DateField
              label={kind === 'dia' ? 'Dia' : 'Semana do dia'}
              value={day}
              onChange={setDay}
              help={kind === 'semana' ? `${formatDate(period.from)} a ${formatDate(period.to)}` : undefined}
            />
          </div>
        )}
        {kind === 'mes' && (
          <div className="w-52">
            <Select label="Mês" value={month} onChange={(e) => setMonth(e.target.value)}>
              {recentMonths().map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </Select>
          </div>
        )}
        {kind === 'intervalo' && (
          <>
            <div className="w-44">
              <DateField label="De" value={from} onChange={setFrom} />
            </div>
            <div className="w-44">
              <DateField label="Até" value={to} onChange={setTo} />
            </div>
          </>
        )}
        <div className="w-60">
          <Select
            label="Pessoas"
            value={everyone ? 'todos' : 'coluna'}
            onChange={(e) => setEveryone(e.target.value === 'todos')}
          >
            <option value="coluna">Só quem tem coluna na Mesa</option>
            <option value="todos">Todos</option>
          </Select>
        </div>
      </div>

      {inverted && <Alert tone="erro">O início do período vem depois do fim.</Alert>}
      {errors.map((e) => (
        <Alert key={e} tone="erro">
          {e}
        </Alert>
      ))}
      {/* sem base nenhuma, quem explica é o estado vazio — o aviso repetiria a frase */}
      {refused.length > 0 && ready.length > 0 && (
        <Alert tone="informacao">
          Você não vê o aproveitamento de {refused.join(' e ')}: lá seu grupo não tem a permissão de
          acompanhar a Mesa.
        </Alert>
      )}
      {merged.trailGaps.length > 0 && (
        <Alert tone="informacao">
          Tocados, puxados, transferências e aproveitamento contam a partir do primeiro movimento
          feito na Mesa (
          {merged.trailGaps
            .map((g) => `${g.tenant}: ${g.since ? `desde ${formatDate(g.since)}` : 'nenhum ainda'}`)
            .join('; ')}
          ). Os pagos que vieram do Kanban entram com quem recebeu o crédito, mas sem esse rastro.
        </Alert>
      )}

      {!loading && ready.length === 0 && errors.length === 0 && !inverted && (
        <EmptyState icon="grafico" title="Nenhum aproveitamento para mostrar">
          Seu grupo não tem a permissão de acompanhar a Mesa em nenhuma das suas bases.
        </EmptyState>
      )}

      {ready.length > 0 && (
        <>
          <StatCardList>
            <StatCard icon="dinheiro" label="Pago cobrança" value={collection.count} support={formatMoney(collection.amount)} />
            <StatCard
              icon="atualizar"
              label="Pago automático"
              value={automatic.count}
              support={`${formatMoney(automatic.amount)} — ${queueAutomatic.count} da fila mãe, sem dono`}
            />
            <StatCard
              icon="grafico"
              label="Total recuperado"
              value={formatMoney(sum(collection.amount, automatic.amount))}
              support={`${closed} card(s) pago(s), inclusive ${formatMoney(queueAutomatic.amount)} do automático da fila mãe`}
            />
            <StatCard
              icon="percentual"
              label="Participação da cobrança"
              value={share === null ? '—' : `${share}%`}
              support={
                creditedShare === null
                  ? 'sobre todos os pagos, inclusive o automático da fila mãe'
                  : `sobre todos os pagos; só entre os cards com dono, como o Kanban contava: ${creditedShare}%`
              }
            />
          </StatCardList>

          <CardGrid>
            <Chart
              kind="barra"
              title="Recuperação por dia"
              descricao="Pago cobrança, na data em que o card foi dado como pago."
              label="Pagos cobrança por dia do período"
              data={merged.recovery}
              empty="Nenhum pago cobrança no período."
              category={(r) => formatDate(r.day).slice(0, 5)}
              series={[{ key: 'pagos', label: 'Pagos cobrança', color: 'fase-verde', value: (r) => r.count }]}
            />
            <Chart
              kind="barra"
              stacked
              title="Pagos por pessoa"
              descricao="Pago automático e pago cobrança creditados a cada pessoa."
              label="Pagos por pessoa no período"
              data={shown.filter((p) => p.paidCollection.count + p.paidAutomatic.count > 0)}
              empty="Ninguém com pago creditado no período."
              category={(p) => p.name}
              series={[
                { key: 'automatico', label: 'Pago automático', color: 'fase-azul', value: (p) => p.paidAutomatic.count },
                { key: 'cobranca', label: 'Pago cobrança', color: 'fase-verde', value: (p) => p.paidCollection.count },
              ]}
            />
          </CardGrid>

          <Card
            title="Resultado por pessoa"
            descricao="Os blocos são a faixa do card no dia do pagamento. Aproveitamento: dos cards que a pessoa moveu no período, quantos ela fechou como pago cobrança."
            bleed
          >
            <Table<MergedPerson>
              label="Resultado de cada pessoa no período"
              empty="Ninguém com coluna na Mesa nestas bases."
              items={shown}
              rowKey={(p) => p.userId}
              paginate={false}
              columns={RESULT_COLUMNS}
            />
            {(merged.unattributed.paidAutomatic.count > 0 || merged.unattributed.paidCollection.count > 0) && (
              <p className="px-4 py-3 text-sm text-texto-suave">
                Sem ninguém creditado: {merged.unattributed.paidAutomatic.count} pago(s) automático(s) (
                {formatMoney(merged.unattributed.paidAutomatic.amount)}) e{' '}
                {merged.unattributed.paidCollection.count} pago(s) cobrança (
                {formatMoney(merged.unattributed.paidCollection.amount)}) — o automático da fila mãe e o
                de quem não tem conta na plataforma.
              </p>
            )}
          </Card>

          <CardGrid>
            <Card title="Carga de agora" descricao="Os cards abertos com cada pessoa neste momento — não depende do período." bleed>
              <Table<MergedPerson>
                label="Cards abertos por pessoa"
                empty="Nenhum card aberto com dono."
                items={merged.people.filter((p) => p.workload.size > 0)}
                rowKey={(p) => p.userId}
                paginate={false}
                columns={WORKLOAD_COLUMNS}
              />
            </Card>
            <Card title="Distribuição de agora" descricao="Os cards abertos por coluna, na fila mãe e com dono." bleed>
              <Table<{ column: LoadColumn; queue: Tally; owned: Tally }>
                label="Cards abertos por coluna"
                empty="Nenhum card aberto."
                items={LOAD_ORDER.flatMap((column) => {
                  const d = merged.distribution.get(column);
                  return d ? [{ column, ...d }] : [];
                })}
                rowKey={(d) => d.column}
                paginate={false}
                columns={DISTRIBUTION_COLUMNS}
              />
            </Card>
          </CardGrid>

          <Card title="Transferências recebidas" descricao="Cards que outra pessoa passou para esta, no período." bleed>
            <Table<MergedPerson>
              label="Transferências recebidas por pessoa"
              empty="Nenhuma transferência no período."
              items={shown.filter((p) => p.transfers.size > 0)}
              rowKey={(p) => p.userId}
              paginate={false}
              columns={[
                { title: 'Pessoa', alignment: 'inicio', cell: (p) => p.name },
                {
                  title: 'Recebeu',
                  alignment: 'fim',
                  cell: (p) => [...p.transfers.values()].reduce((n, c) => n + c, 0),
                },
                {
                  title: 'De quem',
                  alignment: 'inicio',
                  cell: (p) =>
                    [...p.transfers]
                      .sort((a, b) => b[1] - a[1])
                      .map(([giver, count]) => `${nameOf(giver)} (${count})`)
                      .join(', '),
                },
              ]}
            />
          </Card>

          <p className="text-xs text-texto-suave">
            Diferente do dashboard do Kanban: o pago automático tem o valor do card (lá aparecia
            quase sempre zerado), o pago automático da fila mãe não é creditado ao "Sistema", e o
            período é o de Fortaleza, não o do computador de quem abre.
          </p>
        </>
      )}
    </Page>
  );
}

const LOAD_ORDER: LoadColumn[] = [...DESK_BANDS, 'receptiva'];

const columnLabel = (c: LoadColumn) => (c === 'receptiva' ? 'Receptiva' : DESK_BAND_LABELS[c]);

function TallyCell({ tally }: { tally: Tally }) {
  if (tally.count === 0) return <span className="text-texto-suave">—</span>;
  return (
    <span className="whitespace-nowrap">
      {tally.count} · {formatMoney(tally.amount)}
    </span>
  );
}

function Efficiency({ person }: { person: MergedPerson }) {
  const pct = deskEfficiency(person.paidTouched, person.touched);
  if (pct === null) {
    return <span className="text-texto-suave" title="Sem movimento na Mesa no período">—</span>;
  }
  return <Badge tone={pct >= 70 ? 'sucesso' : pct >= 40 ? 'alerta' : 'erro'}>{pct}%</Badge>;
}

const RESULT_COLUMNS: Column<MergedPerson>[] = [
  {
    title: 'Pessoa',
    alignment: 'inicio',
    cell: (p) => (
      <span className="flex flex-wrap items-center gap-2">
        {p.name}
        {!p.listed && <Badge tone="neutro">sem coluna</Badge>}
        {p.activeFlow && p.receptiveFlow && <Badge tone="informacao">Ativa + Receptiva</Badge>}
        {!p.activeFlow && p.receptiveFlow && <Badge tone="informacao">Receptiva</Badge>}
      </span>
    ),
  },
  { title: 'Pago cobrança', alignment: 'fim', cell: (p) => <TallyCell tally={p.paidCollection} /> },
  { title: 'Pago automático', alignment: 'fim', cell: (p) => <TallyCell tally={p.paidAutomatic} /> },
  { title: 'Até 5 dias', alignment: 'fim', cell: (p) => <TallyCell tally={p.blocks.vencimento} /> },
  { title: '6 a 30 dias', alignment: 'fim', cell: (p) => <TallyCell tally={p.blocks.atraso_6_30} /> },
  { title: '31+ dias', alignment: 'fim', cell: (p) => <TallyCell tally={p.blocks.atraso_31_mais} /> },
  { title: 'Receptivo', alignment: 'fim', cell: (p) => <TallyCell tally={p.receptive} /> },
  { title: 'Tocados', alignment: 'fim', cell: (p) => p.touched },
  { title: 'Puxados', alignment: 'fim', cell: (p) => p.pulled },
  { title: 'Aproveitamento', cell: (p) => <Efficiency person={p} /> },
  { title: 'Cadastros', alignment: 'fim', cell: (p) => p.created },
];

const WORKLOAD_COLUMNS: Column<MergedPerson>[] = [
  { title: 'Pessoa', alignment: 'inicio', cell: (p) => p.name },
  {
    title: 'Cards',
    alignment: 'fim',
    cell: (p) => [...p.workload.values()].reduce((n, t) => n + t.count, 0),
  },
  {
    title: 'Valor em carteira',
    alignment: 'fim',
    cell: (p) => formatMoney(sum(...[...p.workload.values()].map((t) => t.amount))),
  },
  {
    title: 'Por coluna',
    alignment: 'inicio',
    cell: (p) =>
      LOAD_ORDER.filter((c) => p.workload.has(c))
        .map((c) => `${columnLabel(c)}: ${p.workload.get(c)!.count}`)
        .join(' · '),
  },
];

const DISTRIBUTION_COLUMNS: Column<{ column: LoadColumn; queue: Tally; owned: Tally }>[] = [
  { title: 'Coluna', alignment: 'inicio', cell: (d) => columnLabel(d.column) },
  { title: 'Na fila mãe', alignment: 'fim', cell: (d) => <TallyCell tally={d.queue} /> },
  { title: 'Com dono', alignment: 'fim', cell: (d) => <TallyCell tally={d.owned} /> },
];
